import { Card, Col, Row, Spin, Popover } from 'antd';
import React, { ReactElement, ReactNode } from 'react';
import { useSmoothScroll } from '../../../../../../../shared/hooks/useSmoothScroll';
import { formattedDate } from '../../../../../../../utils/dateTimeUtils';
import {ReactComponent as HIS_patient_img} from '../../../../../../../assets/images/svg/his_patient.svg';
import { ReactComponent as TickIcon} from '../../../../../../../assets/images/svg/green-tick-icon.svg';
import { LoadingOutlined } from '@ant-design/icons';
import { HIS_CARD_LABELS, HYPHEN_STRING, PREFIXES } from '../../../../../../../shared/constants/crmConstants';
import { Payer } from '../../../../../../../shared/models/payer.model';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../../../store';
import { chunk, isEmpty } from 'lodash';
import { formatDoctorName, getNestedValue, objectArrayToString } from '../../../../service/crmUtils';

const HISCardRedesign = ({ HISPatient, isLoading, cardSelected, hisId }: any): ReactElement => {
  useSmoothScroll('scroll-HIS');
  const configuredPayers = useSelector((state: RootState): Payer[] => state.payerList.allPayers);
  const { HISCardDisplayParameters } = useSelector((state: RootState): any => state?.hospital);

  const memberName = getNestedValue(HISPatient, ['patientName', 'patientDetails.patientName']);
  const lastAdmitDateTime = getNestedValue(HISPatient, ['lastAdmitDateTime', 'treatmentDetails.dateOfAdmission']);
  const lastAdmitDoc = getNestedValue(HISPatient, ['doctorName', 'doctorDetails.doctorName']);
  const departmentName = getNestedValue(HISPatient, ['departmentName']);
  const lineOfTreatment = getNestedValue(HISPatient, ['treatmentDetails.proposedLineOfTreatment',
    'treatmentDetails.proposedLineOfTreatment']);
  const diagnosis = getNestedValue(HISPatient, ['diagnosis', 'treatmentDetails.diagnosis']);
  const dateOfVisit = getNestedValue(HISPatient, ['visitDateTime'] );
  const visitType = getNestedValue(HISPatient, ['visitType']);
  const fcAmount = getNestedValue(HISPatient, ['fcAmount']);
  const fcType = getNestedValue(HISPatient, ['fcType']);
  const insurerName = getNestedValue(HISPatient, ['insurerName', 'patientDetails.insurerName']);
  const HISPayerId = getNestedValue(HISPatient, ['payerID', 'patientDetails.payerID']);

  const payerDetail: Payer | undefined = configuredPayers?.find(
    ({ uniqueIdentifier: identifier }: Payer): boolean => identifier === String(HISPayerId),
  );
  const formattedDateOfVisit = dateOfVisit ? formattedDate(dateOfVisit) : HYPHEN_STRING;
  const formattedDOA = lastAdmitDateTime ? formattedDate(lastAdmitDateTime) : HYPHEN_STRING;
  const isCardSelected = !isLoading && cardSelected;
  const proposedLineOfTreatment = !isEmpty(lineOfTreatment) ? lineOfTreatment.name : HYPHEN_STRING;
  const combinedDiagnosis = Array.isArray(diagnosis) ? objectArrayToString(diagnosis) : diagnosis;
  const formattedDoctorName = (!isEmpty(lastAdmitDoc) && typeof lastAdmitDoc === 'string') ? formatDoctorName(lastAdmitDoc) : HYPHEN_STRING;

  // function to map HIS data with the corresponding labels in the card
  const getFieldValue = (fieldLabel: string): string => {
    const fieldMappings: Record<string, any> = {
      'Doctor Name': formattedDoctorName !== HYPHEN_STRING ?
        PREFIXES.DOCTOR + formattedDoctorName : HYPHEN_STRING,
      'Date of Visit': formattedDateOfVisit,
      'Department': departmentName,
      'Line of Treatment': proposedLineOfTreatment,
      'Visit Type': visitType,
      'Diagnosis': combinedDiagnosis,
      'Date of Admission': formattedDOA,
      'FC Amount': fcAmount,
      'FC Type': fcType,
      'Insurer Name': insurerName,
      'Payer Name': payerDetail?.name,
    };
    return fieldMappings[fieldLabel] || '-';
  };

  // function to get the labels based on HISID, defaults to 0
  const getCardLabels = (id: string | number): any => {
    return HIS_CARD_LABELS[id] || HIS_CARD_LABELS[0];
  };

  const renderCardContent = (): ReactNode => {
    const cardLabels: string[] = isEmpty(HISCardDisplayParameters) ? getCardLabels(hisId) : HISCardDisplayParameters;
    // to create pairs of labels
    const labelPairs = chunk(cardLabels, 2);
    return labelPairs.map((row: string[], rowIndex: number): ReactElement => (
      <Col span={24} key={rowIndex} className="HIS-card_content">
        <Row>
          {row.map((label: string, colIndex: number): ReactElement => (
            <Col span={12} key={`${rowIndex}-${colIndex}`}>
              <div className="his-label-title">{label}</div>
              <div className={`his-value NA-redesign-card-value`}>
                {getFieldValue(label)}
              </div>
            </Col>
          ))}
        </Row>
      </Col>
    ));
  };

  return (
    <Popover
      content={
        <span className="card-tooltip-content">
          { isCardSelected ? 'Click on card to unselect' : 'Click on card to select' }
        </span>}
      placement="top"
    >
      <Card
        data-testid="benefCard"
        className={`scroll-HIS his-card HIS-panel-styles ${cardSelected ? 'patient-card-selected' : ''} card-selected mb-span`}
        hoverable={true}
      >
        <Col className="re-design-selected-Svg-Icon HIS">
          {isCardSelected && <TickIcon/>}
        </Col>
        <Row>
          {/* Header with patient name */}
          <Col span={24} className="his-header">
            <Row align="middle" gutter={6}>
              <Col span={2} className="HIS-doct-img">
                <HIS_patient_img />
              </Col>
              <Col span={22}>
                <div className="his-member-value">{memberName || '-'}</div>
              </Col>
            </Row>
          </Col>
          {renderCardContent()}
        </Row>
        <Row justify="end">
          <div className="spin-container">
            {isLoading && cardSelected && (
              <Spin indicator={<LoadingOutlined className="card-loading-icon" spin={true} />} />
            )}
          </div>
        </Row>
      </Card>
    </Popover>
  );
};

export default HISCardRedesign;
